import React, { useEffect, useState, useMemo } from "react";
import { Form, Col, Row, OverlayTrigger, Tooltip } from "react-bootstrap";
import { mapUserRoles } from "../../../functions/useRoleMapping";
import { useSelector, useDispatch } from "react-redux";
import useHandleBlur from "../../../functions/useHandleBlur";
import useTbdCheck from "../../../functions/useTbdCheck";
import { useFormState } from "../../../functions/useFormState";
import { setJobDetail } from "../../../redux/slices/jobDetailsSlice";
import {
  selectUserDetails,
  selectUserRoles,
} from "../../../redux/slices/userSlice";
import { selectApiUrls } from "../../../redux/slices/apiUrlSlice";
import {
  selectElectricalOptions,
  selectGeneralStation,
  selectPumpManifoldPiping,
  selectFieldDescriptions,
} from "../../../redux/selectors";
import SaveBanner from "../Mechanical/SaveBanner"; // Import SaveBanner component
import { selectApiStatus } from "../../../redux/slices/apiStatusSlice";
import NoRecordFoundAlert from "../../common/NoRecordFoundAlert";

const Options = (zone) => {
  const dispatch = useDispatch();
  const userDetails = useSelector(selectUserDetails);
  const userRoles = useSelector(selectUserRoles);
  const currentUser = userDetails?.name;
  const sectionName = "electricalOptions";

  const apiUrls = useSelector(selectApiUrls);
  const apiUrl = apiUrls[sectionName];
  const tableKey = sectionName;

  const generalStationUrl = apiUrls.generalStation;
  const pumpManifoldPipingUrl = apiUrls.pumpManifoldPiping;

  const storedJobDetails = useSelector(selectElectricalOptions);
  const generalStation = useSelector(selectGeneralStation);
  const pumpManifoldPiping = useSelector(selectPumpManifoldPiping);

  const activetab = useSelector(
    (state) => state.activeMechanicalTab.selectedZone
  );

  const { options } = useSelector((state) => state.dropdown);

  const fieldDescriptions = useSelector(selectFieldDescriptions);

  const getFieldTitle = (fieldName) => {
    return (
      fieldDescriptions.find((desc) => desc.ColumnName === fieldName)
        ?.Description || ""
    );
  };

  const renderTooltip = (fieldName) => (
    <Tooltip id={`tooltip-${fieldName}`}>
      {getFieldTitle(fieldName)
        .split("\n")
        .map((line, index) => (
          <span key={index}>
            {line}
            <br />
          </span>
        ))}
    </Tooltip>
  );

  /****************Add dropdown options here*************************/

  const controlSystemOptions =
    options?.filter((option) => option.DropdownType === "Control System").sort((a, b) => a.SortOrder - b.SortOrder) || [];
  const displayOptions =
    options?.filter((option) => option.DropdownType === "Display").sort((a, b) => a.SortOrder - b.SortOrder) || [];
  const genericOptions =
    options?.filter((option) => option.DropdownType === "Generic Yes/ No").sort((a, b) => a.SortOrder - b.SortOrder) || [];
  const remoteStartRelayOptions =
    options?.filter((option) => option.DropdownType === "Remote Relay").sort((a, b) => a.SortOrder - b.SortOrder) || [];
  const remoteDisableRelayOptions =
    options?.filter((option) => option.DropdownType === "Remote Relay").sort((a, b) => a.SortOrder - b.SortOrder) || [];
  const electricalEnclosurePaintOptions =
    options?.filter((option) => option.DropdownType === "Electrical Enclosure Paint").sort((a, b) => a.SortOrder - b.SortOrder) || [];
  const electricalEnclosureMaterialOptions =
    options?.filter((option) => option.DropdownType === "Electrical Enclosure Material").sort((a, b) => a.SortOrder - b.SortOrder) || [];
  const enclosureCoolingOptions =
    options?.filter((option) => option.DropdownType === "Enclosure Cooling").sort((a, b) => a.SortOrder - b.SortOrder) || [];
  const disconnectTypeOptions =
    options?.filter((option) => option.DropdownType === "Disconnect Type").sort((a, b) => a.SortOrder - b.SortOrder) || [];
  const interiorPanelLightingOptions =
    options?.filter((option) => option.DropdownType === "Interior Panel Lighting").sort((a, b) => a.SortOrder - b.SortOrder) || [];
  const externalLightingOptions =
    options?.filter((option) => option.DropdownType === "External Lighting").sort((a, b) => a.SortOrder - b.SortOrder) || [];
  const flowMeterTypeOptions =
    options?.filter((option) => option.DropdownType === "Flow Meter Type").sort((a, b) => a.SortOrder - b.SortOrder) || [];
  const electricalEnclosureActualSizeOptions =
    options?.filter((option) => option.DropdownType === "Electrical Enclosure Actual Size").sort((a, b) => a.SortOrder - b.SortOrder) || [];
  const disconnectActualSizeOptions =
    options?.filter((option) => option.DropdownType === "Disconnect Actual Size").sort((a, b) => a.SortOrder - b.SortOrder) || [];
  const surgeProtectionOptions =
    options?.filter((option) => option.DropdownType === "Surge Protection").sort((a, b) => a.SortOrder - b.SortOrder) || [];
  const stationHeatingOptions =
    options?.filter((option) => option.DropdownType === "Station Heating").sort((a, b) => a.SortOrder - b.SortOrder) || [];
    const windowKitOptions =
    options?.filter((option) => option.DropdownType === "Window Kit").sort((a, b) => a.SortOrder - b.SortOrder) || [];
    const phDisplayOptions =
      options?.filter((option) => option.DropdownType === "Ph Display").sort((a, b) => a.SortOrder - b.SortOrder) || [];
    const salinityDisplayOptions =
      options?.filter((option) => option.DropdownType === "Salinity Display").sort((a, b) => a.SortOrder - b.SortOrder) || [];
    const panelOrientationOptions =
      options?.filter((option) => option.DropdownType === "Panel Orientation").sort((a, b) => a.SortOrder - b.SortOrder) || [];

  
  /******************************************************************/


  const pumpManifoldPipingDetails =
    pumpManifoldPiping.find((detail) => detail.Zone === activetab) || {};

  const generalStationDetails =
    generalStation.find((detail) => detail.Zone === activetab) || {};

    const apiStatus = useSelector(selectApiStatus); // Fetch API status from Redux
    const loading = apiStatus[sectionName] !== "success"; // Determine loading state based on API status

  const error = null;

  const jobDetails = useMemo(() => {
    const detail = storedJobDetails.find((detail) => detail.Zone === activetab);
    return detail || storedJobDetails[0] || {};
  }, [storedJobDetails, activetab]);

  const { formData, handleChange, setFormData } = useFormState({});

  useEffect(() => {
    if (jobDetails) {
      setFormData({
        ID: jobDetails.ID || "",
        controlSystem: jobDetails.ControlSystem || "",
        display: jobDetails.Display || "",
        optoIsolator: jobDetails.OptoIsolator || "",
        optoCoupler: jobDetails.OptoCoupler || "",
        fertigationRunRelay: jobDetails.FertigationRunRelay || "",
        chemicalTreatmentRelay: jobDetails.ChemicalTreatmentRelay || "",
        phDisplay: jobDetails.PhDisplay || "",
        salinityDisplay: jobDetails.SalinityDisplay || "",
        powerPhaseMonitorProtection:
          jobDetails.PowerPhaseMonitorProtection || "",
        hourMeter: jobDetails.HourMeter || "",
        remoteStartRelay: jobDetails.RemoteStartRelay || "",
        remoteDisableRelay: jobDetails.RemoteDisableRelay || "",
        wattnodeMonitor: jobDetails.WattnodeMonitor || "",
        individualMotorDisconnects: jobDetails.IndividualMotorDisconnects || "",
        deadFront: jobDetails.DeadFront || "",
        electricalEnclosurePaint: jobDetails.ElectricalEnclosurePaint || "",
        electricalEnclosureMaterial:
          jobDetails.ElectricalEnclosureMaterial || "",
        panelOrientation: jobDetails.PanelOrientation || "",
        enclosureCooling: jobDetails.EnclosureCooling || "",
        disconnectType: jobDetails.DisconnectType || "",
        interiorPanelLighting: jobDetails.InteriorPanelLighting || "",
        externalLighting: jobDetails.ExternalLighting || "",
        lightedHOA: jobDetails.LightedHOA || "",
        remoteMountedVFDKeypad: jobDetails.RemoteMountedVFDKeypad || "",
        vfdViewingWindow: jobDetails.VFDViewingWindow || "",
        windowKit: jobDetails.WindowKit || "",
        surgeProtection: jobDetails.SurgeProtection || "",
        stationHeating: jobDetails.StationHeating || "",
        disconnectEstimatedSize: jobDetails.DisconnectEstimatedSize || "",
        disconnectActualSize: jobDetails.DisconnectActualSize || "",
        electricalEnclosureEstimatedSize:
          jobDetails.ElectricalEnclosureEstimatedSize || "",
        electricalEnclosureActualSize:
          jobDetails.ElectricalEnclosureActualSize || "",
        comments: jobDetails.Comments || "",
        remoteDisconnect: jobDetails.RemoteDisconnect || "",
        sunShade: jobDetails.SunShade || "",
        accessoryJunctionBox: jobDetails.AccessoryJunctionBox || "",
        flowMeterType: pumpManifoldPipingDetails.FlowMeterType || "",
        electricalEnclosureLocation:
          generalStationDetails?.ElectricalEnclosureLocation,
        interconnectWire: generalStationDetails.InterconnectWire || "",
        legHeight: generalStationDetails.LegHeight || "",
        additionalJBox: generalStationDetails.AdditionalJBox || "",
      });
    }
  }, [dispatch, jobDetails, setFormData, generalStationDetails.AdditionalJBox, generalStationDetails.ElectricalEnclosureLocation, generalStationDetails.InterconnectWire, generalStationDetails.LegHeight, pumpManifoldPipingDetails.FlowMeterType]);

  const jobCheckoutStatus = useSelector((state) => state.checkout.job);
  const isJobCheckedOut = jobCheckoutStatus?.IsCheckedOut === true;
  const isCurrentUserCheckedOut =
    jobCheckoutStatus?.CheckedOutBy === currentUser;
    const readOnly = useSelector((state) => state.readOnly.isReadOnly); // Fetch read-only state from Redux
  const isDisabled = (isJobCheckedOut && !isCurrentUserCheckedOut) || readOnly; // Update isDisabled condition

  const [roles, setRoles] = useState({
    Admin: false,
    Apps: false,
    Engineer: false,
    TestPit: false,
    PSN: false,
    Sales: false,
    ReadOnly: false,
  });

  const { handleFocus, handleBlur, saveStatus, showNotification } = useHandleBlur(formData);

  useEffect(() => {
    setRoles(mapUserRoles(userRoles));
  }, [userRoles]);

  const { tbdFields } = useTbdCheck(formData, zone);
  const zoneTbdFields = zone ? tbdFields?.[zone] : tbdFields;

  const handleToggleChange = (e) => {
    const newValue = e.target.checked ? "OFF Skid" : "On Station Skid";

    // Update local formData state
    setFormData((prevFormData) => ({
      ...prevFormData,
      electricalEnclosureLocation: newValue,
    }));

    // Dispatch the updated generalStationDetails to the store
    dispatch(
      setJobDetail({
        section: "generalStation",
        key: "ElectricalEnclosureLocation",
        value: newValue,
        zone: activetab,
      })
    );
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    ); // Show loading spinner
  }

  if (error) {
    return <div className="error">{error}</div>; // Show error message if fetch fails
  }

  if (!loading && !jobDetails?.ID) {
    return (
      <NoRecordFoundAlert sectionName={sectionName} zone={zone} />
    );
  }

  return (
    <div>
      {loading ? (
        <div className="d-flex justify-content-center align-items-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : error ? (
        <p style={{ color: "red" }}>Error: {error}</p>
      ) : !formData ? (
        <p>Loading...</p>
      ) : (
        <div>
          <SaveBanner showNotification={showNotification} saveStatus={saveStatus} />
          <div className="mechanical-responsive-grid electrical-options-responsive">
          <Row className="mb-3">
            <Col>
              <Form.Group as={Row} controlId="controlSystem">
                <Form.Label column md={3}>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("ControlSystem")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>Control System</span>
                  </OverlayTrigger>
                </Form.Label>
                <Col>
                  <Form.Select
                    name="controlSystem"
                    value={formData.controlSystem || ""}
                    onFocus={handleFocus}
                    onBlur={(e) =>
                      handleBlur(e, "Control System", controlSystemOptions, {
                        ID: formData.ID,
                        url: apiUrl,
                        field: "ControlSystem",
                        tableKey,
                      })
                    }
                    disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                    className={
                      zoneTbdFields?.controlSystem ? "tbd-highlight" : ""
                    }
                    onChange={handleChange}
                  >
                    <option value="">Select Control System</option>
                    {controlSystemOptions.map((option) => (
                      <option key={option.ID} value={option.DisplayText}>
                        {option.DisplayText}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
              </Form.Group>
            </Col>

            <Col>
              <Form.Group as={Row} controlId="display">
                <Form.Label column md={3}>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("Display")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>Display</span>
                  </OverlayTrigger>
                </Form.Label>
                <Col>
                  <Form.Select
                    name="display"
                    value={formData.display || ""}
                    onFocus={handleFocus}
                    onBlur={(e) =>
                      handleBlur(e, "Display", displayOptions, {
                        ID: formData.ID,
                        url: apiUrl,
                        field: "Display",
                        tableKey,
                      })
                    }
                    disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                    className={zoneTbdFields?.display ? "tbd-highlight" : ""}
                    onChange={handleChange}
                  >
                    <option value="">Select Display</option>
                    {displayOptions.map((option) => (
                      <option key={option.ID} value={option.DisplayText}>
                        {option.DisplayText}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col>
              <Form.Group as={Row} controlId="optoIsolator">
                <Form.Label column md={3}>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("OptoIsolator")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>Opto Isolator</span>
                  </OverlayTrigger>
                </Form.Label>
                <Col>
                  <Form.Select
                    name="optoIsolator"
                    value={formData.optoIsolator || ""}
                    onFocus={handleFocus}
                    onBlur={(e) =>
                      handleBlur(e, "Opto Isolator", genericOptions, {
                        ID: formData.ID,
                        url: apiUrl,
                        field: "OptoIsolator",
                        tableKey,
                      })
                    }
                    disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                    className={
                      zoneTbdFields?.optoIsolator ? "tbd-highlight" : ""
                    }
                    onChange={handleChange}
                  >
                    <option value="">Select Opto Isolator</option>
                    {genericOptions.map((option) => (
                      <option key={option.ID} value={option.DisplayText}>
                        {option.DisplayText}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
              </Form.Group>
            </Col>

            <Col>
              <Form.Group as={Row} controlId="optoCoupler">
                <Form.Label column md={3}>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("OptoCoupler")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>Opto Coupler</span>
                  </OverlayTrigger>
                </Form.Label>
                <Col>
                  <Form.Select
                    name="optoCoupler"
                    value={formData.optoCoupler || ""}
                    onFocus={handleFocus}
                    onBlur={(e) =>
                      handleBlur(e, "Opto Coupler", genericOptions, {
                        ID: formData.ID,
                        url: apiUrl,
                        field: "OptoCoupler",
                        tableKey,
                      })
                    }
                    disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                    className={
                      zoneTbdFields?.optoCoupler ? "tbd-highlight" : ""
                    }
                    onChange={handleChange}
                  >
                    <option value="">Select Opto Coupler</option>
                    {genericOptions.map((option) => (
                      <option key={option.ID} value={option.DisplayText}>
                        {option.DisplayText}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col>
              <Form.Group as={Row} controlId="fertigationRunRelay">
                <Form.Label column md={3}>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("FertigationRunRelay")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>Fertigation Run Relay</span>
                  </OverlayTrigger>
                </Form.Label>
                <Col>
                  <Form.Select
                    name="fertigationRunRelay"
                    value={formData.fertigationRunRelay || ""}
                    onFocus={handleFocus}
                    onBlur={(e) =>
                      handleBlur(e, "Fertigation Run Relay", genericOptions, {
                        ID: formData.ID,
                        url: apiUrl,
                        field: "FertigationRunRelay",
                        tableKey,
                      })
                    }
                    disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                    className={
                      zoneTbdFields?.fertigationRunRelay ? "tbd-highlight" : ""
                    }
                    onChange={handleChange}
                  >
                    <option value="">Select Fertigation Run Relay</option>
                    {genericOptions.map((option) => (
                      <option key={option.ID} value={option.DisplayText}>
                        {option.DisplayText}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
              </Form.Group>
            </Col>

            <Col>
              <Form.Group as={Row} controlId="chemicalTreatmentRelay">
                <Form.Label column md={3}>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("ChemicalTreatmentRelay")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>Station Run Relay</span>
                  </OverlayTrigger>
                </Form.Label>
                <Col>
                  <Form.Select
                    name="chemicalTreatmentRelay"
                    value={formData.chemicalTreatmentRelay || ""}
                    onFocus={handleFocus}
                    onBlur={(e) =>
                      handleBlur(
                        e,
                        "Station Run Relay",
                        genericOptions,
                        {
                          ID: formData.ID,
                          url: apiUrl,
                          field: "ChemicalTreatmentRelay",
                          tableKey,
                        }
                      )
                    }
                    disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                    className={
                      zoneTbdFields?.chemicalTreatmentRelay
                        ? "tbd-highlight"
                        : ""
                    }
                    onChange={handleChange}
                  >
                    <option value="">Select Station Run Relay</option>
                    {genericOptions.map((option) => (
                      <option key={option.ID} value={option.DisplayText}>
                        {option.DisplayText}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col>
              <Form.Group as={Row} controlId="phDisplay">
                <Form.Label column md={3}>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("PhDisplay")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>Ph Display</span>
                  </OverlayTrigger>
                </Form.Label>
                <Col>
                  <Form.Select
                    name="phDisplay"
                    value={formData.phDisplay || ""}
                    onFocus={handleFocus}
                    onBlur={(e) =>
                      handleBlur(e, "Ph Display", phDisplayOptions, {
                        ID: formData.ID,
                        url: apiUrl,
                        field: "PhDisplay",
                        tableKey,
                      })
                    }
                    disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                    className={zoneTbdFields?.phDisplay ? "tbd-highlight" : ""}
                    onChange={handleChange}
                  >
                    <option value="">Select Ph Display</option>
                    {phDisplayOptions.map((option) => (
                      <option key={option.ID} value={option.DisplayText}>
                        {option.DisplayText}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
              </Form.Group>
            </Col>

            <Col>
              <Form.Group as={Row} controlId="salinityDisplay">
                <Form.Label column md={3}>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("SalinityDisplay")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>Salinity Display</span>
                  </OverlayTrigger>
                </Form.Label>
                <Col>
                  <Form.Select
                    name="salinityDisplay"
                    value={formData.salinityDisplay || ""}
                    onFocus={handleFocus}
                    onBlur={(e) =>
                      handleBlur(e, "Salinity Display", salinityDisplayOptions, {
                        ID: formData.ID,
                        url: apiUrl,
                        field: "SalinityDisplay",
                        tableKey,
                      })
                    }
                    disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                    className={
                      zoneTbdFields?.salinityDisplay ? "tbd-highlight" : ""
                    }
                    onChange={handleChange}
                  >
                    <option value="">Select Salinity Display</option>
                    {salinityDisplayOptions.map((option) => (
                      <option key={option.ID} value={option.DisplayText}>
                        {option.DisplayText}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col>
              <Form.Group as={Row} controlId="powerPhaseMonitorProtection">
                <Form.Label column md={3}>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("PowerPhaseMonitorProtection")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>Power Phase Monitor Protection</span>
                  </OverlayTrigger>
                </Form.Label>
                <Col>
                  <Form.Select
                    name="powerPhaseMonitorProtection"
                    value={formData.powerPhaseMonitorProtection || ""}
                    onFocus={handleFocus}
                    onBlur={(e) =>
                      handleBlur(
                        e,
                        "Power Phase Monitor Protection",
                        genericOptions,
                        {
                          ID: formData.ID,
                          url: apiUrl,
                          field: "PowerPhaseMonitorProtection",
                          tableKey,
                        }
                      )
                    }
                    disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                    className={
                      zoneTbdFields?.powerPhaseMonitorProtection
                        ? "tbd-highlight"
                        : ""
                    }
                    onChange={handleChange}
                  >
                    <option value="">
                      Select Power Phase Monitor Protection
                    </option>
                    {genericOptions.map((option) => (
                      <option key={option.ID} value={option.DisplayText}>
                        {option.DisplayText}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
              </Form.Group>
            </Col>

            <Col>
              <Form.Group as={Row} controlId="hourMeter">
                <Form.Label column md={3}>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("HourMeter")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>Hour Meter</span>
                  </OverlayTrigger>
                </Form.Label>
                <Col>
                  <Form.Select
                    name="hourMeter"
                    value={formData.hourMeter || ""}
                    onFocus={handleFocus}
                    onBlur={(e) =>
                      handleBlur(e, "Hour Meter", genericOptions, {
                        ID: formData.ID,
                        url: apiUrl,
                        field: "HourMeter",
                        tableKey,
                      })
                    }
                    disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                    className={zoneTbdFields?.hourMeter ? "tbd-highlight" : ""}
                    onChange={handleChange}
                  >
                    <option value="">Select Hour Meter</option>
                    {genericOptions.map((option) => (
                      <option key={option.ID} value={option.DisplayText}>
                        {option.DisplayText}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col>
              <Form.Group as={Row} controlId="remoteStartRelay">
                <Form.Label column md={3}>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("RemoteStartRelay")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>Remote Start Relay</span>
                  </OverlayTrigger>
                </Form.Label>
                <Col>
                  <Form.Select
                    name="remoteStartRelay"
                    value={formData.remoteStartRelay || ""}
                    onFocus={handleFocus}
                    onBlur={(e) =>
                      handleBlur(
                        e,
                        "Remote Start Relay",
                        remoteStartRelayOptions,
                        {
                          ID: formData.ID,
                          url: apiUrl,
                          field: "RemoteStartRelay",
                          tableKey,
                        }
                      )
                    }
                    disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                    className={
                      zoneTbdFields?.remoteStartRelay ? "tbd-highlight" : ""
                    }
                    onChange={handleChange}
                  >
                    <option value="">Select Remote Start Relay</option>
                    {remoteStartRelayOptions.map((option) => (
                      <option key={option.ID} value={option.DisplayText}>
                        {option.DisplayText}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
              </Form.Group>
            </Col>

            <Col>
              <Form.Group as={Row} controlId="remoteDisableRelay">
                <Form.Label column md={3}>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("RemoteDisableRelay")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>Remote Disable Relay</span>
                  </OverlayTrigger>
                </Form.Label>
                <Col>
                  <Form.Select
                    name="remoteDisableRelay"
                    value={formData.remoteDisableRelay || ""}
                    onFocus={handleFocus}
                    onBlur={(e) =>
                      handleBlur(
                        e,
                        "Remote Disable Relay",
                        remoteDisableRelayOptions,
                        {
                          ID: formData.ID,
                          url: apiUrl,
                          field: "RemoteDisableRelay",
                          tableKey,
                        }
                      )
                    }
                    disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                    className={
                      zoneTbdFields?.remoteDisableRelay ? "tbd-highlight" : ""
                    }
                    onChange={handleChange}
                  >
                    <option value="">Select Remote Disable Relay</option>
                    {remoteDisableRelayOptions.map((option) => (
                      <option key={option.ID} value={option.DisplayText}>
                        {option.DisplayText}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col>
              <Form.Group as={Row} controlId="remoteDisconnect">
                <Form.Label column md={3}>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("RemoteDisconnect")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>Remote Disconnect</span>
                  </OverlayTrigger>
                </Form.Label>
                <Col>
                  <Form.Select
                    name="remoteDisconnect"
                    value={formData.remoteDisconnect || ""}
                    onFocus={handleFocus}
                    onBlur={(e) =>
                      handleBlur(e, "Remote Disconnect", genericOptions, {
                        ID: formData.ID,
                        url: apiUrl,
                        field: "RemoteDisconnect",
                        tableKey,
                      })
                    }
                    disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                    className={
                      zoneTbdFields?.remoteDisconnect ? "tbd-highlight" : ""
                    }
                    onChange={handleChange}
                  >
                    <option value="">Select Remote Disconnect</option>
                    {genericOptions.map((option) => (
                      <option key={option.ID} value={option.DisplayText}>
                        {option.DisplayText}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
              </Form.Group>
            </Col>
            <Col>
              <Form.Group as={Row} controlId="sunShade">
                <Form.Label column md={3}>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("SunShade")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>Sun Shade</span>
                  </OverlayTrigger>
                </Form.Label>
                <Col>
                  <Form.Select
                    name="sunShade"
                    value={formData.sunShade || ""}
                    onFocus={handleFocus}
                    onBlur={(e) =>
                      handleBlur(e, "Sun Shade", genericOptions, {
                        ID: formData.ID,
                        url: apiUrl,
                        field: "SunShade",
                        tableKey,
                      })
                    }
                    disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                    className={
                      zoneTbdFields?.sunShade ? "tbd-highlight" : ""
                    }
                    onChange={handleChange}
                  >
                    <option value="">Select Sun Shade</option>
                    {genericOptions.map((option) => (
                      <option key={option.ID} value={option.DisplayText}>
                        {option.DisplayText}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col>
              <Form.Group as={Row} controlId="wattnodeMonitor">
                <Form.Label column md={3}>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("WattnodeMonitor")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>Wattnode Monitor</span>
                  </OverlayTrigger>
                </Form.Label>
                <Col>
                  <Form.Select
                    name="wattnodeMonitor"
                    value={formData.wattnodeMonitor || ""}
                    onFocus={handleFocus}
                    onBlur={(e) =>
                      handleBlur(e, "Wattnode Monitor", genericOptions, {
                        ID: formData.ID,
                        url: apiUrl,
                        field: "WattnodeMonitor",
                        tableKey,
                      })
                    }
                    disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                    className={
                      zoneTbdFields?.wattnodeMonitor ? "tbd-highlight" : ""
                    }
                    onChange={handleChange}
                  >
                    <option value="">Select Wattnode Monitor</option>
                    {genericOptions.map((option) => (
                      <option key={option.ID} value={option.DisplayText}>
                        {option.DisplayText}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
              </Form.Group>
            </Col>

            <Col>
              <Form.Group as={Row} controlId="individualMotorDisconnects">
                <Form.Label column md={3}>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("IndividualMotorDisconnects")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>Individual Motor Disconnects</span>
                  </OverlayTrigger>
                </Form.Label>
                <Col>
                  <Form.Select
                    name="individualMotorDisconnects"
                    value={formData.individualMotorDisconnects || ""}
                    onFocus={handleFocus}
                    onBlur={(e) =>
                      handleBlur(
                        e,
                        "Individual Motor Disconnects",
                        genericOptions,
                        {
                          ID: formData.ID,
                          url: apiUrl,
                          field: "IndividualMotorDisconnects",
                          tableKey,
                        }
                      )
                    }
                    disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                    className={
                      zoneTbdFields?.individualMotorDisconnects
                        ? "tbd-highlight"
                        : ""
                    }
                    onChange={handleChange}
                  >
                    <option value="">
                      Select Individual Motor Disconnects
                    </option>
                    {genericOptions.map((option) => (
                      <option key={option.ID} value={option.DisplayText}>
                        {option.DisplayText}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col>
              <Form.Group as={Row} controlId="deadFront">
                <Form.Label column md={3}>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("DeadFront")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>Dead Front</span>
                  </OverlayTrigger>
                </Form.Label>
                <Col>
                  <Form.Select
                    name="deadFront"
                    value={formData.deadFront || ""}
                    onFocus={handleFocus}
                    onBlur={(e) =>
                      handleBlur(e, "Dead Front", genericOptions, {
                        ID: formData.ID,
                        url: apiUrl,
                        field: "DeadFront",
                        tableKey,
                      })
                    }
                    disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                    className={zoneTbdFields?.deadFront ? "tbd-highlight" : ""}
                    onChange={handleChange}
                  >
                    <option value="">Select Dead Front</option>
                    {genericOptions.map((option) => (
                      <option key={option.ID} value={option.DisplayText}>
                        {option.DisplayText}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
              </Form.Group>
            </Col>

            <Col>
              <Form.Group as={Row} controlId="electricalEnclosurePaint">
                <Form.Label column md={3}>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("ElectricalEnclosurePaint")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>Electrical Enclosure Paint</span>
                  </OverlayTrigger>
                </Form.Label>
                <Col>
                  <Form.Select
                    name="electricalEnclosurePaint"
                    value={formData.electricalEnclosurePaint || ""}
                    onFocus={handleFocus}
                    onBlur={(e) =>
                      handleBlur(
                        e,
                        "Electrical Enclosure Paint",
                        electricalEnclosurePaintOptions,
                        {
                          ID: formData.ID,
                          url: apiUrl,
                          field: "ElectricalEnclosurePaint",
                          tableKey,
                        }
                      )
                    }
                    disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                    className={
                      zoneTbdFields?.electricalEnclosurePaint
                        ? "tbd-highlight"
                        : ""
                    }
                    onChange={handleChange}
                  >
                    <option value="">Select Electrical Enclosure Paint</option>
                    {electricalEnclosurePaintOptions.map((option) => (
                      <option key={option.ID} value={option.DisplayText}>
                        {option.DisplayText}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col>
              <Form.Group as={Row} controlId="electricalEnclosureMaterial">
                <Form.Label column md={3}>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("ElectricalEnclosureMaterial")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>Electrical Enclosure Material</span>
                  </OverlayTrigger>
                </Form.Label>
                <Col>
                  <Form.Select
                    name="electricalEnclosureMaterial"
                    value={formData.electricalEnclosureMaterial || ""}
                    onFocus={handleFocus}
                    onBlur={(e) =>
                      handleBlur(
                        e,
                        "Electrical Enclosure Material",
                        electricalEnclosureMaterialOptions,
                        {
                          ID: formData.ID,
                          url: apiUrl,
                          field: "ElectricalEnclosureMaterial",
                          tableKey,
                        }
                      )
                    }
                    disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                    className={
                      zoneTbdFields?.electricalEnclosureMaterial
                        ? "tbd-highlight"
                        : ""
                    }
                    onChange={handleChange}
                  >
                    <option value="">
                      Select Electrical Enclosure Material
                    </option>
                    {electricalEnclosureMaterialOptions.map((option) => (
                      <option key={option.ID} value={option.DisplayText}>
                        {option.DisplayText}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
              </Form.Group>
            </Col>

            <Col>
              <Form.Group as={Row} controlId="enclosureCooling">
                <Form.Label column md={3}>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("EnclosureCooling")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>Enclosure Cooling</span>
                  </OverlayTrigger>
                </Form.Label>
                <Col>
                  <Form.Select
                    name="enclosureCooling"
                    value={formData.enclosureCooling || ""}
                    onFocus={handleFocus}
                    onBlur={(e) =>
                      handleBlur(
                        e,
                        "Enclosure Cooling",
                        enclosureCoolingOptions,
                        {
                          ID: formData.ID,
                          url: apiUrl,
                          field: "EnclosureCooling",
                          tableKey,
                        }
                      )
                    }
                    disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                    className={
                      zoneTbdFields?.enclosureCooling ? "tbd-highlight" : ""
                    }
                    onChange={handleChange}
                  >
                    <option value="">Select Enclosure Cooling</option>
                    {enclosureCoolingOptions.map((option) => (
                      <option key={option.ID} value={option.DisplayText}>
                        {option.DisplayText}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col>
              <Form.Group as={Row} controlId="panelOrientation">
                <Form.Label column md={3}>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("PanelOrientation")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>Panel Orientation</span>
                  </OverlayTrigger>
                </Form.Label>
                <Col>
                  <Form.Select
                    name="panelOrientation"
                    value={formData.panelOrientation || ""}
                    onFocus={handleFocus}
                    onBlur={(e) =>
                      handleBlur(
                        e,
                        "Panel Orientation",
                        panelOrientationOptions,
                        {
                          ID: formData.ID,
                          url: apiUrl,
                          field: "PanelOrientation",
                          tableKey,
                        }
                      )
                    }
                    disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                    className={
                      zoneTbdFields?.panelOrientation
                        ? "tbd-highlight"
                        : !String(formData.panelOrientation ?? "").trim() &&
                            (roles.Admin || roles.Apps)
                          ? "blank-highlight"
                          : ""
                    }
                    onChange={handleChange}
                  >
                    <option value="">Select Panel Orientation</option>
                    {panelOrientationOptions.map((option) => (
                      <option key={option.ID} value={option.DisplayText}>
                        {option.DisplayText}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
              </Form.Group>
            </Col>

            <Col>
              <Form.Group as={Row} controlId="disconnectType">
                <Form.Label column md={3}>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("DisconnectType")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>Disconnect Type</span>
                  </OverlayTrigger>
                </Form.Label>
                <Col>
                  <Form.Select
                    name="disconnectType"
                    value={formData.disconnectType || ""}
                    onFocus={handleFocus}
                    onBlur={(e) =>
                      handleBlur(e, "Disconnect Type", disconnectTypeOptions, {
                        ID: formData.ID,
                        url: apiUrl,
                        field: "DisconnectType",
                        tableKey,
                      })
                    }
                    disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                    className={
                      zoneTbdFields?.disconnectType ? "tbd-highlight" : ""
                    }
                    onChange={handleChange}
                  >
                    <option value="">Select Disconnect Type</option>
                    {disconnectTypeOptions.map((option) => (
                      <option key={option.ID} value={option.DisplayText}>
                        {option.DisplayText}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col>
              <Form.Group as={Row} controlId="interiorPanelLighting">
                <Form.Label column md={3}>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("InteriorPanelLighting")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>Interior Panel Lighting</span>
                  </OverlayTrigger>
                </Form.Label>
                <Col>
                  <Form.Select
                    name="interiorPanelLighting"
                    value={formData.interiorPanelLighting || ""}
                    onFocus={handleFocus}
                    onBlur={(e) =>
                      handleBlur(
                        e,
                        "Interior Panel Lighting",
                        interiorPanelLightingOptions,
                        {
                          ID: formData.ID,
                          url: apiUrl,
                          field: "InteriorPanelLighting",
                          tableKey,
                        }
                      )
                    }
                    disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                    className={
                      zoneTbdFields?.interiorPanelLighting
                        ? "tbd-highlight"
                        : ""
                    }
                    onChange={handleChange}
                  >
                    <option value="">Select Interior Panel Lighting</option>
                    {interiorPanelLightingOptions.map((option) => (
                      <option key={option.ID} value={option.DisplayText}>
                        {option.DisplayText}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
              </Form.Group>
            </Col>

            <Col>
              <Form.Group as={Row} controlId="externalLighting">
                <Form.Label column md={3}>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("ExternalLighting")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>External Lighting</span>
                  </OverlayTrigger>
                </Form.Label>
                <Col>
                  <Form.Select
                    name="externalLighting"
                    value={formData.externalLighting || ""}
                    onFocus={handleFocus}
                    onBlur={(e) =>
                      handleBlur(
                        e,
                        "External Lighting",
                        externalLightingOptions,
                        {
                          ID: formData.ID,
                          url: apiUrl,
                          field: "ExternalLighting",
                          tableKey,
                        }
                      )
                    }
                    disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                    className={
                      zoneTbdFields?.externalLighting ? "tbd-highlight" : ""
                    }
                    onChange={handleChange}
                  >
                    <option value="">Select External Lighting</option>
                    {externalLightingOptions.map((option) => (
                      <option key={option.ID} value={option.DisplayText}>
                        {option.DisplayText}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col>
              <Form.Group as={Row} controlId="lightedHOA">
                <Form.Label column md={3}>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("LightedHOA")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>Lighted HOA</span>
                  </OverlayTrigger>
                </Form.Label>
                <Col>
                  <Form.Select
                    name="lightedHOA"
                    value={formData.lightedHOA || ""}
                    onFocus={handleFocus}
                    onBlur={(e) =>
                      handleBlur(e, "Lighted HOA", genericOptions, {
                        ID: formData.ID,
                        url: apiUrl,
                        field: "LightedHOA",
                        tableKey,
                      })
                    }
                    disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                    className={zoneTbdFields?.lightedHOA ? "tbd-highlight" : ""}
                    onChange={handleChange}
                  >
                    <option value="">Select Lighted HOA</option>
                    {genericOptions.map((option) => (
                      <option key={option.ID} value={option.DisplayText}>
                        {option.DisplayText}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
              </Form.Group>
            </Col>

            <Col>
              <Form.Group as={Row} controlId="remoteMountedVFDKeypad">
                <Form.Label column md={3}>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("RemoteMountedVFDKeypad")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>Remote Mounted VFD Keypad</span>
                  </OverlayTrigger>
                </Form.Label>
                <Col>
                  <Form.Select
                    name="remoteMountedVFDKeypad"
                    value={formData.remoteMountedVFDKeypad || ""}
                    onFocus={handleFocus}
                    onBlur={(e) =>
                      handleBlur(
                        e,
                        "Remote Mounted VFD Keypad",
                        genericOptions,
                        {
                          ID: formData.ID,
                          url: apiUrl,
                          field: "RemoteMountedVFDKeypad",
                          tableKey,
                        }
                      )
                    }
                    disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                    className={
                      zoneTbdFields?.remoteMountedVFDKeypad
                        ? "tbd-highlight"
                        : ""
                    }
                    onChange={handleChange}
                  >
                    <option value="">Select Remote Mounted VFD Keypad</option>
                    {genericOptions.map((option) => (
                      <option key={option.ID} value={option.DisplayText}>
                        {option.DisplayText}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col>
              <Form.Group as={Row} controlId="vfdViewingWindow">
                <Form.Label column md={3}>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("VFDViewingWindow")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>VFD Viewing Window</span>
                  </OverlayTrigger>
                </Form.Label>
                <Col>
                  <Form.Select
                    name="vfdViewingWindow"
                    value={formData.vfdViewingWindow || ""}
                    onFocus={handleFocus}
                    onBlur={(e) =>
                      handleBlur(e, "VFD Viewing Window", genericOptions, {
                        ID: formData.ID,
                        url: apiUrl,
                        field: "VFDViewingWindow",
                        tableKey,
                      })
                    }
                    disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                    className={
                      zoneTbdFields?.vfdViewingWindow ? "tbd-highlight" : ""
                    }
                    onChange={handleChange}
                  >
                    <option value="">Select VFD Viewing Window</option>
                    {genericOptions.map((option) => (
                      <option key={option.ID} value={option.DisplayText}>
                        {option.DisplayText}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
              </Form.Group>
            </Col>

            <Col>
              <Form.Group as={Row} controlId="windowKit">
                <Form.Label column md={3}>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("WindowKit")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>Window Kit</span>
                  </OverlayTrigger>
                </Form.Label>
                <Col>
                  <Form.Select
                    name="windowKit"
                    value={formData.windowKit || ""}
                    onFocus={handleFocus}
                    onBlur={(e) =>
                      handleBlur(e, "Window Kit", windowKitOptions, {
                        ID: formData.ID,
                        url: apiUrl,
                        field: "WindowKit",
                        tableKey,
                      })
                    }
                    disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                    className={zoneTbdFields?.windowKit ? "tbd-highlight" : ""}
                    onChange={handleChange}
                  >
                    <option value="">Select Window Kit</option>
                    {windowKitOptions.map((option) => (
                      <option key={option.ID} value={option.DisplayText}>
                        {option.DisplayText}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col>
              <Form.Group as={Row} controlId="surgeProtection">
                <Form.Label column md={3}>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("SurgeProtection")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>Surge Protection</span>
                  </OverlayTrigger>
                </Form.Label>
                <Col>
                  <Form.Select
                    name="surgeProtection"
                    value={formData.surgeProtection || ""}
                    onFocus={handleFocus}
                    onBlur={(e) =>
                      handleBlur(e, "Surge Protection", surgeProtectionOptions, {
                        ID: formData.ID,
                        url: apiUrl,
                        field: "SurgeProtection",
                        tableKey,
                      })
                    }
                    disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                    className={
                      zoneTbdFields?.surgeProtection ? "tbd-highlight" : ""
                    }
                    onChange={handleChange}
                  >
                    <option value="">Select Surge Protection</option>
                    {surgeProtectionOptions.map((option) => (
                      <option key={option.ID} value={option.DisplayText}>
                        {option.DisplayText}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
              </Form.Group>
            </Col>

            <Col>
              <Form.Group as={Row} controlId="stationHeating">
                <Form.Label column md={3}>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("StationHeating")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>Station Heating</span>
                  </OverlayTrigger>
                </Form.Label>
                <Col>
                  <Form.Select
                    name="stationHeating"
                    value={formData.stationHeating || ""}
                    onFocus={handleFocus}
                    onBlur={(e) =>
                      handleBlur(e, "Station Heating", stationHeatingOptions, {
                        ID: formData.ID,
                        url: apiUrl,
                        field: "StationHeating",
                        tableKey,
                      })
                    }
                    disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                    className={zoneTbdFields?.stationHeating ? "tbd-highlight" : ""}
                    onChange={handleChange}
                  >
                    <option value="">Select Station Heating</option>
                    {stationHeatingOptions.map((option) => (
                      <option key={option.ID} value={option.DisplayText}>
                        {option.DisplayText}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col>
              <Form.Group as={Row} controlId="flowMeterType">
                <Form.Label column md={3}>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("FlowMeterType")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>Flow Meter Type</span>
                  </OverlayTrigger>
                </Form.Label>
                <Col>
                  <Form.Select
                    name="flowMeterType"
                    value={formData.flowMeterType || ""}
                    onFocus={handleFocus}
                    onBlur={(e) =>
                      handleBlur(e, "Flow Meter Type", flowMeterTypeOptions, {
                        ID: pumpManifoldPipingDetails.ID,
                        url: pumpManifoldPipingUrl,
                        field: "FlowMeterType",
                        tableKey,
                      })
                    }
                    disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                    className={
                      zoneTbdFields?.flowMeterType ? "tbd-highlight" : ""
                    }
                    onChange={handleChange}
                  >
                    <option value="">Select Flow Meter Type</option>
                    {flowMeterTypeOptions.map((option) => (
                      <option key={option.ID} value={option.DisplayText}>
                        {option.DisplayText}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
              </Form.Group>
            </Col>

            <Col>
              <Form.Group as={Row} controlId="accessoryJunctionBox">
                <Form.Label column md={3}>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("AccessoryJunctionBox")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>Accessory Junction Box</span>
                  </OverlayTrigger>
                </Form.Label>
                <Col>
                  <Form.Select
                    name="accessoryJunctionBox"
                    value={formData.accessoryJunctionBox || ""}
                    onFocus={handleFocus}
                    onBlur={(e) =>
                      handleBlur(e, "Accessory Junction Box", genericOptions, {
                        ID: formData.ID,
                        url: apiUrl,
                        field: "AccessoryJunctionBox",
                        tableKey,
                      })
                    }
                    disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                    className={
                      zoneTbdFields?.accessoryJunctionBox ? "tbd-highlight" : ""
                    }
                    onChange={handleChange}
                  >
                    <option value="">Select Accessory Junction Box</option>
                    {genericOptions.map((option) => (
                      <option key={option.ID} value={option.DisplayText}>
                        {option.DisplayText}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-3 mechanical-form-full-width">
            <Col>
              <Form.Group controlId="comments">
                <Form.Label column>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("Comments")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>Comments</span>
                  </OverlayTrigger>
                </Form.Label>
                <Col>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    name="comments"
                    value={formData.comments || ""}
                    onFocus={handleFocus}
                    onBlur={(e) =>
                      handleBlur(e, "Comments", [], {
                        ID: formData.ID,
                        url: apiUrl,
                        field: "Comments",
                        tableKey,
                      })
                    }
                    disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                    className={zoneTbdFields?.comments ? "tbd-highlight" : ""}
                    onChange={handleChange}
                    placeholder="Enter Comments"
                  />
                </Col>
              </Form.Group>
            </Col>
          </Row>
          <h4 className="subsection-header mb-3">Sale Quote Estimate</h4>
          <Row className="mb-3">
            <Col>
              <Form.Group as={Row} controlId="electricalEnclosureEstimatedSize">
                <Form.Label column md={3}>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("ElectricalEnclosureEstimatedSize")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>Electrical Enclosure Estimated Size</span>
                  </OverlayTrigger>
                </Form.Label>
                <Col>
                  <Form.Control
                    type="text"
                    name="electricalEnclosureEstimatedSize"
                    value={formData.electricalEnclosureEstimatedSize || ""}
                    onFocus={handleFocus}
                    onBlur={(e) =>
                      handleBlur(e, "Electrical Enclosure Estimated Size", [], {
                        ID: formData.ID,
                        url: apiUrl,
                        field: "ElectricalEnclosureEstimatedSize",
                        tableKey,
                      })
                    }
                    disabled={
                      true || isDisabled || (!roles.Admin && !roles.Apps)
                    }
                    className={
                      zoneTbdFields?.electricalEnclosureEstimatedSize
                        ? "tbd-highlight"
                        : ""
                    }
                    onChange={handleChange}
                    placeholder="Enter Electrical Enclosure Estimated Size"
                  />
                </Col>
              </Form.Group>
            </Col>

            <Col>
              <Form.Group as={Row} controlId="disconnectEstimatedSize">
                <Form.Label column md={3}>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("DisconnectEstimatedSize")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>Disconnect Estimated Size</span>
                  </OverlayTrigger>
                </Form.Label>
                <Col>
                  <Form.Control
                    type="text"
                    name="disconnectEstimatedSize"
                    value={formData.disconnectEstimatedSize || ""}
                    onFocus={handleFocus}
                    onBlur={(e) =>
                      handleBlur(e, "Disconnect Estimated Size", [], {
                        ID: formData.ID,
                        url: apiUrl,
                        field: "DisconnectEstimatedSize",
                        tableKey,
                      })
                    }
                    disabled={
                      true || isDisabled || (!roles.Admin && !roles.Apps)
                    }
                    className={
                      zoneTbdFields?.disconnectEstimatedSize
                        ? "tbd-highlight"
                        : ""
                    }
                    onChange={handleChange}
                    placeholder="Enter Disconnect Estimated Size"
                  />
                </Col>
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col>
              <Form.Group as={Row} controlId="electricalEnclosureActualSize">
                <Form.Label column md={3}>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("ElectricalEnclosureActualSize")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>Electrical Enclosure Actual Size</span>
                  </OverlayTrigger>
                </Form.Label>
                <Col>
                  <Form.Select
                    name="electricalEnclosureActualSize"
                    value={formData.electricalEnclosureActualSize || ""}
                    onFocus={handleFocus}
                    onBlur={(e) =>
                      handleBlur(
                        e,
                        "Electrical Enclosure Actual Size",
                        electricalEnclosureActualSizeOptions,
                        {
                          ID: formData.ID,
                          url: apiUrl,
                          field: "ElectricalEnclosureActualSize",
                          tableKey,
                        }
                      )
                    }
                    disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                    className={
                      zoneTbdFields?.electricalEnclosureActualSize
                        ? "tbd-highlight"
                        : ""
                    }
                    onChange={handleChange}
                  >
                    <option value="">
                      Select Electrical Enclosure Actual Size
                    </option>
                    {electricalEnclosureActualSizeOptions.map((option) => (
                      <option key={option.ID} value={option.DisplayText}>
                        {option.DisplayText}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
              </Form.Group>
            </Col>

            <Col>
              <Form.Group as={Row} controlId="disconnectActualSize">
                <Form.Label column md={3}>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("DisconnectActualSize")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>Disconnect Actual Size</span>
                  </OverlayTrigger>
                </Form.Label>
                <Col>
                  <Form.Select
                    name="disconnectActualSize"
                    value={formData.disconnectActualSize || ""}
                    onFocus={handleFocus}
                    onBlur={(e) =>
                      handleBlur(
                        e,
                        "Disconnect Actual Size",
                        disconnectActualSizeOptions,
                        {
                          ID: formData.ID,
                          url: apiUrl,
                          field: "DisconnectActualSize",
                          tableKey,
                        }
                      )
                    }
                    disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                    className={
                      zoneTbdFields?.disconnectActualSize ? "tbd-highlight" : ""
                    }
                    onChange={handleChange}
                  >
                    <option value="">Select Disconnect Actual Size</option>
                    {disconnectActualSizeOptions.map((option) => (
                      <option key={option.ID} value={option.DisplayText}>
                        {option.DisplayText}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3 align-items-center justify-content-center">
            <h4 className="subsection-header mb-3 d-flex align-items-center">
              Off Skid Panel
              <Form.Check
                className="toggle ms-3"
                type="switch"
                id="electricalEnclosureLocation"
                name="electricalEnclosureLocation"
                checked={formData.electricalEnclosureLocation === "OFF Skid"} // Checked when value is "OFF Skid"
                onFocus={handleFocus}
                onBlur={(e) => {
                  handleBlur(
                    {
                      target: {
                        name: "electricalEnclosureLocation",
                        value:
                          formData.electricalEnclosureLocation === "OFF Skid"
                            ? "OFF Skid"
                            : "On Station Skid",
                      },
                    },
                    "Electrical Enclosure Location",
                    [],
                    {
                      ID: generalStationDetails.ID,
                      url: generalStationUrl,
                      field: "ElectricalEnclosureLocation",
                      tableKey,
                    }
                  );
                }}
                disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                onChange={(e) => {
                  handleChange({
                    target: {
                      name: "electricalEnclosureLocation",
                      value: e.target.checked ? "OFF Skid" : "On Station Skid",
                    },
                  });
                  handleToggleChange(e);
                }}
              />
            </h4>
          </Row>

          {formData.electricalEnclosureLocation === "OFF Skid" && (
            <>
              <Row className="mb-3">
                <Col>
                  <Form.Group as={Row} controlId="interconnectWire">
                    <Form.Label column md={3}>
                      <OverlayTrigger
                        placement="top"
                        overlay={renderTooltip("InterconnectWire")}
                        delay={{ show: 300, hide: 200 }}
                      >
                        <span>Interconnect Wire</span>
                      </OverlayTrigger>
                    </Form.Label>
                    <Col>
                      <Form.Control
                        type="text"
                        name="interconnectWire"
                        value={formData.interconnectWire || ""}
                        onFocus={handleFocus}
                        onBlur={(e) =>
                          handleBlur(e, "Interconnect Wire", [], {
                            ID: generalStationDetails.ID,
                            url: generalStationUrl,
                            field: "InterconnectWire",
                            tableKey,
                          })
                        }
                        disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                        className={
                          zoneTbdFields?.interconnectWire ? "tbd-highlight" : ""
                        }
                        onChange={handleChange}
                        placeholder="Enter Interconnect Wire"
                      />
                    </Col>
                  </Form.Group>
                </Col>

                <Col>
                  <Form.Group as={Row} controlId="legHeight">
                    <Form.Label column md={3}>
                      <OverlayTrigger
                        placement="top"
                        overlay={renderTooltip("LegHeight")}
                        delay={{ show: 300, hide: 200 }}
                      >
                        <span>Leg Height</span>
                      </OverlayTrigger>
                    </Form.Label>
                    <Col>
                      <Form.Control
                        type="text"
                        name="legHeight"
                        value={formData.legHeight || ""}
                        onFocus={handleFocus}
                        onBlur={(e) =>
                          handleBlur(e, "Leg Height", [], {
                            ID: generalStationDetails.ID,
                            url: generalStationUrl,
                            field: "LegHeight",
                            tableKey,
                          })
                        }
                        disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                        className={
                          zoneTbdFields?.legHeight ? "tbd-highlight" : ""
                        }
                        onChange={handleChange}
                        placeholder="Enter Leg Height"
                      />
                    </Col>
                  </Form.Group>
                </Col>

                <Col>
                  <Form.Group as={Row} controlId="additionalJBox">
                    <Form.Label column md={3}>
                      <OverlayTrigger
                        placement="top"
                        overlay={renderTooltip("AdditionalJBox")}
                        delay={{ show: 300, hide: 200 }}
                      >
                        <span>Motor Leads Junction Box</span>
                      </OverlayTrigger>
                    </Form.Label>
                    <Col>
                      <Form.Select
                        name="additionalJBox"
                        value={formData.additionalJBox || ""}
                        onFocus={handleFocus}
                        onBlur={(e) =>
                          handleBlur(e, "Motor Leads Junction Box", genericOptions, {
                            ID: generalStationDetails.ID,
                            url: generalStationUrl,
                            field: "AdditionalJBox",
                            tableKey,
                          })
                        }
                        disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                        className={
                          zoneTbdFields?.additionalJBox ? "tbd-highlight" : ""
                        }
                        onChange={handleChange}
                      >
                        <option value="">Select Motor Leads Junction Box</option>
                        {genericOptions.map((option) => (
                          <option key={option.ID} value={option.DisplayText}>
                            {option.DisplayText}
                          </option>
                        ))}
                      </Form.Select>
                    </Col>
                  </Form.Group>
                </Col>
              </Row>
            </>
          )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Options;
